// =====================================================================
// src/screens/v2/ConnectList.jsx
//
// V2 Connect List screen.
//
// Shows businesses the user has connected with. Each card shows status
// (Active / Lapsed / Pending), last activity, and quick action buttons
// (Order again / Open profile).
//
// Cross-screen sync: connections set in BusinessProfile via localStorage
// are reflected here (and vice versa).
//
// In Phase B, MOCK data is replaced by Firestore queries on the
// `connections` collection filtered by current user.
//
// Props:
//   onBack          — return to where V2App came from (exits V2)
//   onOpenBusiness  — called with businessId to navigate to Profile
//   onOpenChat      — opens chats screen (placeholder for now)
// =====================================================================

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Search, X, MessageSquare, MapPin, Check,
  SlidersHorizontal, ChevronDown
} from 'lucide-react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import {
  getUserConnections,
  getBusiness,
  logoBgFromCategory,
  relativeTime,
} from '../../lib/v2/firestore';
// Centralised override-aware open-status helper. Wraps the hours-only
// openStatus() with the merchant's manualOpenOverride field — keeps
// the open/closed pill consistent with the merchant's "Open today"
// toggle on MerchantApp.
import { getEffectiveOpenStatus } from '../../lib/v2/openStatusOverride';
// Session 6c1.5 — customer-side inactive gating: reuse the same helper
// used by BusinessProfile + BusinessDirectory.
import { isBusinessActive } from '../../lib/v2/isBusinessActive';
import './ConnectList.css';

// ============================================================
// transformConnection
// ============================================================
//
// Combines a Firestore connection doc with its business doc into the
// card-ready shape the JSX below expects. Mirrors the field names used
// by the original MOCK_CONNECTIONS so the JSX is untouched.
//
// `lastActivityType` / `lastActivityStr` / `activityTs` are reserved
// for when posts/orders are wired (Step 3.5+); for now they stay null.
//
function transformConnection(conn, biz) {
  // Override-aware open status (Item #13 follow-up). Replaces the
  // inline manualOpenOverride application we previously duplicated
  // here; centralised in openStatusOverride.js. Without the override
  // honoured, a merchant who toggled themselves closed could still
  // receive Place-Order taps because ConnectList was computing
  // open-status from hours alone.
  const status = getEffectiveOpenStatus(biz);
  const createdMs = conn.createdAt?.toMillis?.() ?? 0;

  return {
    id: biz.id,
    name: biz.name,
    type: biz.shortDescription || '',
    locality: biz.locality || '',
    city: biz.city || '',
    logo: biz.logo || '🏪',
    logoBg: logoBgFromCategory(biz.category),
    verified: !!biz.verified,
    // Connection status (active only — query filters the rest)
    status: conn.status,
    // Y9: business-level status preserved so the 'Pending' filter chip can
    // surface businesses whose verification is still pending. Connection
    // status and business status are independent concepts — a connection
    // can be active to a pending_verification business (Path C policy).
    businessStatus: biz.status,
    // Session 6c1.5 — derive activation state from the business doc so
    // the tabs (Active / Pending / Lapsed) can put inactive-connected
    // businesses in the Lapsed bucket regardless of pending/verified.
    // Pure derivation, no extra reads.
    bizActive: isBusinessActive(biz),
    // Open status — manual override (today only) wins over hours.
    open: status.open,
    openTimeStr: status.openMeta,
    // Activity data — not wired yet
    lastActivityType: null,
    lastActivityStr: '',
    activityTs: null,
    // Connected metadata — from the connection doc
    connectedAt: relativeTime(conn.createdAt),
    connectedTs: createdMs,
  };
}


// ============================================================
// classifyConnection(b)  — Session 6c1.5
// ============================================================
//
// Returns 'active' | 'pending' | 'lapsed' — the tab bucket the
// connection belongs to. Mutual exclusion is guaranteed by the
// early-return ladder: precedence Lapsed > Pending > Active.
//
// Precedence rationale (customer's POV):
//   - LAPSED: "I can't transact right now" — either the biz is
//     inactive (paid + trial windows both lapsed) OR the connection
//     itself was marked lapsed at the connection-doc level. Either
//     cause maps to the same customer experience, so we merge them.
//   - PENDING: "This biz is in trial, verification still in progress"
//     — biz IS active (in-trial), just not verified yet. Can still
//     transact per Path C policy.
//   - ACTIVE: "Fully live" — verified biz + active connection + in
//     paid activation window (or still in trial).
//
// Any biz that doesn't fit any bucket (e.g. suspended businessStatus)
// returns null. Currently no tab surfaces those; if one is added later,
// extend this classifier rather than the counts/filter call sites.
function classifyConnection(b) {
  // Lapsed FIRST — takes precedence over both other buckets. A pending
  // biz whose trial expired lands in Lapsed, not Pending.
  if (b.status === 'lapsed' || !b.bizActive) return 'lapsed';
  if (b.businessStatus === 'pending_verification') return 'pending';
  if (b.businessStatus === 'verified' && b.status === 'active') return 'active';
  return null;
}


// ============================================================
// COMPONENT
// ============================================================

export default function ConnectList({ onBack, onOpenBusiness, onOpenChat, onBrowseBusinesses, onOpenCart, onOpenChatList }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [toast, setToast] = useState(null);

  // ============ DATA LAYER (Step 3.6) ============
  // Connections loaded from Firestore: fetch user's active connections,
  // then fetch each referenced business in parallel, then merge into the
  // card-ready shape via transformConnection.
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadConnections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const conns = await getUserConnections();
      if (conns.length === 0) {
        setConnections([]);
        return;
      }
      // Fetch each business doc in parallel
      const bizDocs = await Promise.all(
        conns.map(c => getBusiness(c.businessId).catch(() => null))
      );
      // Skip any whose business doc is missing/deleted
      const merged = conns
        .map((c, i) => bizDocs[i] ? transformConnection(c, bizDocs[i]) : null)
        .filter(Boolean);
      setConnections(merged);
    } catch (err) {
      console.error('[ConnectList] load failed:', err);
      setError(err?.message || 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // 6.6d: ConnectList was built before the onAuthStateChanged race-fix
    // pattern (Steps 6.0a / 6.5). On a fresh page load (?v2=connect-list),
    // the component mounted BEFORE Firebase Auth resolved auth.currentUser
    // — getUserConnections() read a null uid and returned empty, surfacing
    // as "No connections yet" even when the user had 3 connections.
    // Now: wait for auth state, then call loadConnections.
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setConnections([]);
        setLoading(false);
        return;
      }
      loadConnections();
    });
    return unsubscribe;
  }, [loadConnections]);

  // Auto-hide toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  // Counts per status (used in filter chip badges)
  const counts = useMemo(() => {
    // Session 6c1.5 — Categorization uses classifyConnection() below,
    // which enforces MUTUAL EXCLUSION so a connection appears in exactly
    // one tab (All + Active + Pending + Lapsed always sum to total, no
    // double-counting). Precedence: Lapsed > Pending > Active.
    //
    // A connection lands in Lapsed if the underlying business is no longer
    // active (paid subscription + trial both lapsed) OR the connection was
    // marked lapsed at the connection level. Rationale: from the customer's
    // POV, "I can't transact with this biz right now" is the same signal
    // regardless of whether the connection or the biz caused it.
    return {
      all: connections.length,
      active: connections.filter(b => classifyConnection(b) === 'active').length,
      lapsed: connections.filter(b => classifyConnection(b) === 'lapsed').length,
      pending: connections.filter(b => classifyConnection(b) === 'pending').length,
    };
  }, [connections]);

  // Apply filter + search + sort
  const visible = useMemo(() => {
    let list = connections;

    // Status filter — uses the same classifier as counts so filter
    // results always match the badge numbers exactly.
    if (statusFilter === 'active') {
      list = list.filter(b => classifyConnection(b) === 'active');
    } else if (statusFilter === 'pending') {
      list = list.filter(b => classifyConnection(b) === 'pending');
    } else if (statusFilter === 'lapsed') {
      list = list.filter(b => classifyConnection(b) === 'lapsed');
    }
    // 'all' → no filter

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(b =>
        b.name.toLowerCase().includes(q) ||
        b.type.toLowerCase().includes(q)
      );
    }

    // Sort — activity data not wired yet, so 'recent' falls back to connectedTs
    const sorted = [...list];
    if (sortBy === 'recent' || sortBy === 'connected') {
      // Newest connection first (descending by createdAt millis)
      sorted.sort((a, b) => b.connectedTs - a.connectedTs);
    } else if (sortBy === 'alphabetical') {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    }
    return sorted;
  }, [connections, statusFilter, searchQuery, sortBy]);

  const sortLabel = sortBy === 'recent' ? 'Recent activity'
    : sortBy === 'alphabetical' ? 'Alphabetical'
    : 'Recently connected';

  function handleOrderAgain(e, business) {
    e.stopPropagation();
    if (!business.open) {
      setToast(`${business.name} is closed right now`);
      return;
    }
    // I4: route to cart (was a stub toast). The cart screen handles
    // its own business-load + transactability check, so we don't
    // pre-validate further here.
    if (onOpenCart) {
      onOpenCart(business.id);
    } else {
      setToast('Cart unavailable');
    }
  }

  function handleOpenProfile(e, businessId) {
    e.stopPropagation();
    onOpenBusiness?.(businessId);
  }

  function handleCardTap(businessId) {
    onOpenBusiness?.(businessId);
  }

  function handleSortChoice(value) {
    setSortBy(value);
    setSortMenuOpen(false);
  }

  const totalConnected = connections.length;
  // Activity data not wired yet (will come with posts/orders) — stays 0 for now
  const recentActiveCount = connections.filter(b => b.activityTs != null && b.activityTs <= 7).length;
  const lapsedCount = counts.lapsed;

  return (
    <div className="v2-connect-list">
      {/* ============ TOP BAR ============ */}
      <div className="v2cl-top-bar">
        <button className="v2cl-back-btn" onClick={onBack} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <div className="v2cl-top-info">
          <div className="v2cl-top-title">Your Connect List</div>
          <div className="v2cl-top-sub">
            {totalConnected === 0
              ? 'No connections yet'
              : `${totalConnected} ${totalConnected === 1 ? 'business' : 'businesses'} connected`}
          </div>
        </div>
        <button
          className="v2cl-chat-btn"
          onClick={() => {
            // Path Z: open V1 ChatsList on the BusinessConnect tab.
            // onOpenChatList exits V2 + sets sessionStorage flags
            // (see V2App). The old hardcoded '2' badge was a stub;
            // a real unread count will return when /chats unread
            // tracking is wired across all message subcollections.
            if (onOpenChatList) {
              onOpenChatList();
            } else {
              setToast('Chat list unavailable');
            }
          }}
          aria-label="Messages"
        >
          <MessageSquare size={18} />
        </button>
      </div>

      {loading ? (
        /* ============ LOADING ============ */
        <div className="v2cl-empty-state">
          <div className="v2cl-empty-icon">⏳</div>
          <div className="v2cl-empty-title">Loading your connections…</div>
        </div>
      ) : error ? (
        /* ============ ERROR ============ */
        <div className="v2cl-empty-state">
          <div className="v2cl-empty-icon">⚠️</div>
          <div className="v2cl-empty-title">Couldn't load connections</div>
          <p className="v2cl-empty-text">{error}</p>
          <button className="v2cl-empty-cta" onClick={loadConnections}>
            Retry
          </button>
        </div>
      ) : totalConnected === 0 ? (
        /* ============ EMPTY (no connections at all) ============ */
        <div className="v2cl-empty-state">
          <div className="v2cl-empty-icon">🌟</div>
          <div className="v2cl-empty-title">No connections yet</div>
          <p className="v2cl-empty-text">
            Connect with businesses you trust and want to keep close.
            Their posts, hours, and updates will all live here.
          </p>
          <button
            className="v2cl-empty-cta"
            onClick={() => onBrowseBusinesses?.() || setToast('Browse the Local Businesses screen')}
          >
            Browse local businesses
          </button>
        </div>
      ) : (
        <>
          {/* Y8: Browse pill — always visible at top, no scroll required.
              Inline-styled to avoid touching ConnectList.css. */}
          {onBrowseBusinesses && (
            <div style={{
              padding: '10px 16px 0',
              display: 'flex',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={onBrowseBusinesses}
                style={{
                  background: 'transparent',
                  border: '1px solid #C45A3F',
                  borderRadius: 999,
                  color: '#C45A3F',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: '6px 14px',
                }}
              >
                Browse local businesses →
              </button>
            </div>
          )}

          {/* ============ SEARCH ============ */}
          <div className="v2cl-search-section">
            <div className="v2cl-search-bar">
              <Search size={16} />
              <input
                type="text"
                className="v2cl-search-input"
                placeholder="Search your Connect List…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="v2cl-search-clear"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          {/* ============ FILTER + SORT ============ */}
          <div className="v2cl-filter-bar">
            <button
              className={`v2cl-filter-chip ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All <span className="v2cl-filter-count">{counts.all}</span>
            </button>
            <button
              className={`v2cl-filter-chip ${statusFilter === 'active' ? 'active' : ''}`}
              onClick={() => setStatusFilter('active')}
            >
              Active <span className="v2cl-filter-count">{counts.active}</span>
            </button>
            <button
              className={`v2cl-filter-chip ${statusFilter === 'lapsed' ? 'active' : ''}`}
              onClick={() => setStatusFilter('lapsed')}
            >
              Lapsed <span className="v2cl-filter-count">{counts.lapsed}</span>
            </button>
            <button
              className={`v2cl-filter-chip ${statusFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setStatusFilter('pending')}
            >
              Pending <span className="v2cl-filter-count">{counts.pending}</span>
            </button>
            <div className="v2cl-sort-wrapper">
              <button
                className="v2cl-sort-btn"
                onClick={() => setSortMenuOpen(o => !o)}
              >
                <SlidersHorizontal size={12} />
                <span>{sortLabel}</span>
                <ChevronDown size={11} />
              </button>
              {sortMenuOpen && (
                <>
                  <div className="v2cl-sort-backdrop" onClick={() => setSortMenuOpen(false)} />
                  <div className="v2cl-sort-menu">
                    {[
                      { value: 'recent', label: 'Recent activity' },
                      { value: 'alphabetical', label: 'Alphabetical (A–Z)' },
                      { value: 'connected', label: 'Recently connected' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        className={`v2cl-sort-item ${sortBy === opt.value ? 'selected' : ''}`}
                        onClick={() => handleSortChoice(opt.value)}
                      >
                        <span>{opt.label}</span>
                        {sortBy === opt.value && <Check size={14} strokeWidth={2.5} />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ============ STATS STRIP ============ */}
          <div className="v2cl-stats-strip">
            <span><b>{totalConnected}</b> businesses</span>
            <span className="v2cl-stats-dot">·</span>
            <span><b>{recentActiveCount}</b> with recent activity</span>
            {lapsedCount > 0 && (
              <>
                <span className="v2cl-stats-dot">·</span>
                <span><b>{lapsedCount}</b> lapsed</span>
              </>
            )}
          </div>

          {/* ============ BUSINESS LIST ============ */}
          <div className="v2cl-list">
            {visible.length === 0 ? (
              <div className="v2cl-empty-results">
                <div className="v2cl-empty-results-icon">🔍</div>
                <div className="v2cl-empty-results-title">
                  No matches in this filter
                </div>
                <div className="v2cl-empty-results-sub">
                  Try a different filter or clear your search
                </div>
              </div>
            ) : (
              visible.map(b => (
                <div
                  key={b.id}
                  className={`v2cl-biz-card v2cl-status-${classifyConnection(b) || b.status}`}
                  onClick={() => handleCardTap(b.id)}
                >
                  <div className="v2cl-biz-top">
                    <div
                      className="v2cl-biz-logo"
                      style={{ background: b.logoBg }}
                    >
                      {b.logo}
                    </div>
                    <div className="v2cl-biz-info">
                      <div className="v2cl-biz-name-row">
                        <span className="v2cl-biz-name">{b.name}</span>
                        {/* Session 6c1.5 — Precedence: Inactive badge wins
                            over verified check and Pending badge. Matches
                            BusinessProfile behavior. */}
                        {!b.bizActive ? (
                          <span style={{
                            background: '#E8DDC6',
                            color: '#6E6F84',
                            fontSize: 9,
                            fontWeight: 600,
                            padding: '2px 6px',
                            borderRadius: 6,
                            letterSpacing: '0.3px',
                            textTransform: 'uppercase',
                          }}>
                            Inactive
                          </span>
                        ) : (
                          <>
                            {/* Y9: verified checkmark only for verified businesses */}
                            {b.verified && b.businessStatus === 'verified' && (
                              <span className="v2cl-verified">
                                <Check size={9} strokeWidth={3} />
                              </span>
                            )}
                            {/* Y9: 'Pending' badge for pending_verification businesses.
                                Inline-styled to avoid touching ConnectList.css. */}
                            {b.businessStatus === 'pending_verification' && (
                              <span style={{
                                background: '#FBE8D6',
                                color: '#C45A3F',
                                fontSize: 9,
                                fontWeight: 600,
                                padding: '2px 6px',
                                borderRadius: 6,
                                letterSpacing: '0.3px',
                                textTransform: 'uppercase',
                              }}>
                                Pending
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      <div className="v2cl-biz-type">{b.type}</div>
                      <div className="v2cl-biz-locality">
                        <MapPin size={9} strokeWidth={2.2} />
                        <span>{b.locality}, {b.city}</span>
                      </div>
                    </div>
                  </div>

                  <div className="v2cl-status-row">
                    {/* Session 6c1.5 — Inactive pill takes highest precedence.
                        Matches BusinessProfile treatment: calm/muted grey
                        rather than danger red — "temporarily on pause" not
                        "broken". */}
                    {!b.bizActive ? (
                      <span className="v2cl-pill v2cl-pill-inactive">⏸ Temporarily inactive</span>
                    ) : b.businessStatus === 'pending_verification' ? (
                      <span style={{
                        background: '#F7EBD0',
                        color: '#B8893A',
                        fontSize: 11,
                        fontWeight: 500,
                        padding: '3px 8px',
                        borderRadius: 999,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                      }}>
                        ⏳ Verification pending
                      </span>
                    ) : (
                      <>
                        {b.status === 'active' && (
                          <span className="v2cl-pill v2cl-pill-open">🟢 {b.openTimeStr}</span>
                        )}
                        {b.status === 'lapsed' && (
                          <span className="v2cl-pill v2cl-pill-lapsed">⚠ Not taking orders</span>
                        )}
                      </>
                    )}
                    <span className="v2cl-last-activity">{b.lastActivityStr}</span>
                  </div>

                  <div className="v2cl-actions">
                    {/* Y9: Order CTA shows for both verified and pending biz
                        (both transactable per Path C). Lapsed gets Notify them.
                        Session 6c1.5: also require b.bizActive — an inactive
                        biz (trial + activation both lapsed) shouldn't accept
                        orders. When !bizActive, the "Notify me when active"
                        button (below) replaces the primary Order button. */}
                    {(b.businessStatus === 'verified' || b.businessStatus === 'pending_verification')
                      && b.status === 'active' && b.open && b.bizActive && (
                      <button
                        className="v2cl-action-btn primary"
                        onClick={e => handleOrderAgain(e, b)}
                      >
                        {b.lastActivityType === 'order' && b.lastActivityStr
                          ? '🛒 Order again'
                          : '🛒 Place order'}
                      </button>
                    )}
                    {/* Session 6c1.5 — Notify me when active. Shown when the
                        biz is inactive (regardless of pending / verified /
                        open state). Navigates to BusinessProfile where the
                        actual openSubscribers subscribe/unsubscribe UX lives
                        (writes go through subscribeToOpenAlert there). We
                        deliberately don't inline the write here — keeps the
                        primary subscription flow in one place. */}
                    {!b.bizActive && (
                      <button
                        className="v2cl-action-btn primary"
                        onClick={e => { e.stopPropagation(); handleOpenProfile(e, b.id); }}
                      >
                        🔔 Notify me when active
                      </button>
                    )}
                    {/* Removed: a "🔔 Notify them" button that toasted
                        "Nudge sent" and wrote nothing. This branch is the
                        connection-lapsed / biz-active case; a real action here
                        would be "Reconnect" (connectToBusiness), not a fake
                        nudge. Left as Open profile only for now. */}
                    <button
                      className="v2cl-action-btn"
                      onClick={e => handleOpenProfile(e, b.id)}
                    >
                      Open profile
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* ============ TOAST ============ */}
      {toast && <div className="v2cl-toast">{toast}</div>}
    </div>
  );
}
